import { recordPath } from '@/ui';

import type { SectionPlan } from './planTypes';
import { dayLabel, dueClause, forAmount, instantLabel, withNote, words } from './rowText';

export type OperationsSection =
  | 'UNASSIGNED_TODAY'
  | 'ON_HOLD'
  | 'READY_TO_INVOICE'
  | 'INVOICES_TO_SEND'
  | 'OVERDUE_INVOICES'
  | 'PAYROLL'
  | 'LOW_STOCK';

const INVOICES = '/invoices';

/** The board opens the job on its own, whichever day it falls on. */
function boardJob(jobId: string): string {
  return `/dispatch?job=${encodeURIComponent(jobId)}`;
}

/** The sections of work that keep the business running: the board, billing, payroll and stock. */
export const OPERATIONS_PLANS: Readonly<Record<OperationsSection, SectionPlan>> = {
  UNASSIGNED_TODAY: {
    title: "Today's jobs with no technician",
    seeAllTo: () => '/dispatch',
    row: (item, context) => {
      const start = instantLabel(item.dueAt, context.zone);
      return {
        subject: item.title,
        predicate: `job scheduled for ${start ?? 'today'} has no technician`,
        action: { label: 'Assign', to: boardJob(item.recordId) },
      };
    },
  },
  ON_HOLD: {
    title: 'Jobs on hold',
    // The list's default range is the month; a job on hold is wanted whichever day it falls on.
    seeAllTo: () => '/jobs?status=ON_HOLD&range=all',
    row: (item) => ({
      subject: item.title,
      predicate: withNote('job is on hold', 'described as', item.detail),
      action: { label: 'Review job', to: recordPath('job', item.recordId) },
    }),
  },
  READY_TO_INVOICE: {
    title: 'Jobs ready to invoice',
    seeAllTo: () => INVOICES,
    row: (item, context) => ({
      subject: item.title,
      predicate: words('finished job is ready to invoice', forAmount(item, context)),
      action: { label: 'Invoice job', to: INVOICES },
    }),
  },
  INVOICES_TO_SEND: {
    title: 'Invoices to send',
    seeAllTo: () => `${INVOICES}?status=DRAFT`,
    row: (item, context) => ({
      subject: item.title,
      predicate: words('draft invoice', forAmount(item, context), 'has not been sent'),
      action: { label: 'Send invoice', to: recordPath('invoice', item.recordId) },
    }),
  },
  OVERDUE_INVOICES: {
    title: 'Overdue invoices',
    seeAllTo: () => `${INVOICES}?status=OVERDUE`,
    row: (item, context) => ({
      subject: item.title,
      predicate: words(
        'invoice',
        forAmount(item, context),
        dueClause(item, context) ?? 'is overdue',
      ),
      action: { label: 'Chase payment', to: recordPath('invoice', item.recordId) },
    }),
  },
  PAYROLL: {
    title: 'Payroll to finish',
    seeAllTo: () => '/payroll',
    row: (item) => {
      const end = item.dueOn === null ? null : dayLabel(item.dueOn);
      return {
        subject: item.title,
        predicate: words(
          'pay period',
          end === null ? null : `ending ${end}`,
          'still needs finishing',
        ),
        action: { label: 'Open payroll', to: '/payroll' },
      };
    },
  },
  LOW_STOCK: {
    title: 'Low stock',
    seeAllTo: () => '/inventory?low=true',
    row: (item) => ({
      subject: item.title,
      predicate: 'stock is at or below the reorder point',
      action: { label: 'Open material', to: `/inventory/${encodeURIComponent(item.recordId)}` },
    }),
  },
};
